import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo, faqItems, siteConfig } from '@/mocks/content';

function AboutSchemaScript() {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bytvstream.github.io' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://bytvstream.github.io/about' },
        ],
      },
      {
        '@type': 'Organization',
        name: 'bytv',
        url: 'https://bytvstream.github.io',
        description: siteConfig.description,
        foundingDate: '2018',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+86-400-888-2888',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
          email: 'support@bytv.com',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    setFormStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u3ncd3pcjqs2fcsldg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* noop */ }

      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed as Record<string, unknown> | null)?.['meta'] && typeof (parsed as Record<string, unknown> | null)?.['meta'] === 'object'
            ? String(((parsed as Record<string, unknown>)?.['meta'] as Record<string, string>)?.message || '')
            : (parsed && typeof (parsed as Record<string, unknown>).message === 'string'
              ? String((parsed as Record<string, unknown>).message)
              : responseText);
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
        <input
          id="contact-name"
          name="name"
          type="text"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
          placeholder="请输入您的邮箱地址"
        />
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
          placeholder="请输入咨询主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
          placeholder="请输入您的留言（最多500字）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>

      <div className="anti-spam-field">
        <input
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
      >
        {submitting ? '提交中...' : '发送留言'}
      </button>

      {formStatus === 'success' && (
        <div className="p-4 rounded-lg bg-accent-50 text-accent-800 text-sm font-medium">
          感谢您的留言！我们会尽快与您联系。
        </div>
      )}
      {formStatus === 'error' && (
        <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm font-medium">
          {formError}
        </div>
      )}
    </form>
  );
}

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <AboutSchemaScript />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </nav>

          <section className="mb-16">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-8">关于 bytv</h1>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              <div className="lg:col-span-3">
                <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
                  {aboutInfo.brandIntro.split('\n\n').map((para, i) => (
                    <p key={i} className="mb-4">{para}</p>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="text-base font-heading text-foreground-900 mb-5">平台数据</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {aboutInfo.stats.map((stat, i) => (
                      <div key={i} className="text-center p-4 bg-background-50 rounded-lg">
                        <div className="text-2xl font-bold text-primary-600 mb-1">{stat.value}</div>
                        <div className="text-xs text-foreground-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8">荣誉资质</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {aboutInfo.honors.map((honor, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-background-100 rounded-xl hover:bg-background-200/80 transition-colors duration-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-award-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{honor.title}</h3>
                    <p className="text-xs text-foreground-500">
                      {honor.organization} · {honor.year}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">support@bytv.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
                    <p className="text-sm text-foreground-600">400-888-BYTV (2888)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">服务时间</h4>
                    <p className="text-sm text-foreground-600">每天 9:00 - 21:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-600">北京市朝阳区建国路88号SOHO现代城A座</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6">
                <h3 className="text-base font-heading text-foreground-900 mb-5">在线留言</h3>
                <ContactForm />
              </div>
            </div>
          </section>

          <section className="py-12 border-t border-background-200">
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                常见问题
              </h2>
              <div className="max-w-2xl mx-auto text-left space-y-3">
                {faqItems.slice(0, 4).map((item, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                      <strong>{item.question}</strong>
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link
                  to="/"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  返回首页了解更多 &rarr;
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}